import { Button, Flex, Text } from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { useNavigate, useParams } from "react-router-dom";
import { CreateLeadBlock } from "@/components/LeadBlock/CreateLeadBlock/CreateLeadBlock";
import { Layout } from "@/components/Layout/Layout";
import { useFormik } from "formik";
import { LeadForm } from "@/pages/CreateLeadPage";
import { useEffect, useState, useMemo } from "react";
import { Lead, LeadContact, LeadForm as ModelLeadForm } from "@/model/lead";
import api from "@/api";

export const UpdateLeadPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [currentLead, setCurrentLead] = useState<Lead | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [recruitersOptions, setRecruitersOptions] = useState<{ value: number; label: string }[]>([]);
  const [contacts, setContacts] = useState<LeadContact[]>([]);

  useEffect(() => {
    api.recruiters.recruiters({ customer: true }).then((res) => {
      const resultRecruiters = res.data.map((el: any) => ({
        value: el.id,
        label: el.username,
      }));
      setRecruitersOptions(resultRecruiters);
    });
  }, []);

  useEffect(() => {
    api.leads
      .leadContacts()
      .then((res) => {
        setContacts(res.data);
      })
      .catch((err) => {
        console.error("Ошибка при загрузке контактов:", err);
      });
  }, []);

  useEffect(() => {
    const loadLead = async () => {
      if (!id) return;
      setIsLoading(true);
      try {
        const response = await api.leads.lead({
          params: {
            id: Number(id),
            expand: ["status", "responsible_user", "source", "contact", "site", "comments", "nda", "form", "nda_call", "created_at"],
          },
        });
        setCurrentLead(response.data);
      } catch (error) {
        setCurrentLead(null);
      } finally {
        setIsLoading(false);
      }
    };
    loadLead();
  }, [id]);

  const transformFormToApi = (values: LeadForm): ModelLeadForm | null => {
    if (values.responsible_user_id === null || values.status === null) {
      return null;
    }

    const statusId = Number(values.status);
    const sourceId = values.source ? Number(values.source) : null;

    if (isNaN(statusId) || (sourceId !== null && isNaN(sourceId))) {
      return null;
    }

    return {
      name: values.name,
      responsible_user_id: values.responsible_user_id,
      status_id: statusId,
      contact_id: values.contact_id || null,
      source_id: sourceId,
      site: values.website || null,
      comments: values.comment || null,
      nda: values.nda === "yes" ? true : values.nda === "no" ? false : null,
      form: values.questionnaire === "yes" ? true : values.questionnaire === "no" ? false : null,
      nda_call: values.nda_call === "yes" ? true : values.nda_call === "no" ? false : null,
    };
  };

  const handleUpdate = async () => {
    if (!id || formik.values.name === "" || formik.values.responsible_user_id === null || formik.values.status === null || !hasChanges) {
      return;
    }

    setIsUpdating(true);
    try {
      const apiData = transformFormToApi(formik.values);
      if (!apiData) {
        return;
      }
      const response = await api.leads.leadUpdate({
        id: Number(id),
        body: apiData,
      });
      if (response.data?.id) {
        navigate(`/leads/${response.data.id}`);
      }
    } catch (error) {
      console.error("Ошибка при обновлении лида:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const formik = useFormik<LeadForm>({
    initialValues: {
      name: "",
      contact_id: null,
      responsible_user_id: null,
      source: null,
      website: "",
      comment: "",
      nda: "no",
      questionnaire: "no",
      nda_call: "no",
      status: null,
    },
    onSubmit: handleUpdate,
  });

  const initialLeadValues = useMemo((): LeadForm | null => {
    if (!currentLead || recruitersOptions.length === 0) return null;

    const responsibleUserId = currentLead.responsible_user?.id ?? null;
    const contactId = currentLead.contact?.id ?? null;
    const sourceId = currentLead.source?.id ? String(currentLead.source.id) : null;
    const statusId = currentLead.status?.id ? String(currentLead.status.id) : null;

    return {
      name: currentLead.name ?? "",
      contact_id: contactId,
      responsible_user_id: responsibleUserId,
      source: sourceId,
      website: currentLead.site ?? "",
      comment: currentLead.comments ?? "",
      nda: currentLead.nda ? "yes" : "no",
      questionnaire: currentLead.form ? "yes" : "no",
      nda_call: currentLead.nda_call ? "yes" : "no",
      status: statusId,
    };
  }, [currentLead, recruitersOptions]);

  const hasChanges = useMemo(() => {
    if (!initialLeadValues) return false;

    const current = formik.values;
    const initial = initialLeadValues;

    return (
      current.name !== initial.name ||
      current.contact_id !== initial.contact_id ||
      current.responsible_user_id !== initial.responsible_user_id ||
      current.source !== initial.source ||
      current.website !== initial.website ||
      current.comment !== initial.comment ||
      current.nda !== initial.nda ||
      current.questionnaire !== initial.questionnaire ||
      current.nda_call !== initial.nda_call ||
      current.status !== initial.status
    );
  }, [formik.values, initialLeadValues]);

  useEffect(() => {
    if (initialLeadValues && !formik.dirty) {
      formik.resetForm({ values: initialLeadValues });
    }
  }, [initialLeadValues, formik.dirty]);

  if (isLoading) {
    return (
      <Layout>
        <Flex justifyContent="center" alignItems="center" minH="50vh">
          <Text>Загрузка...</Text>
        </Flex>
      </Layout>
    );
  }

  if (!currentLead) {
    return (
      <Layout>
        <Flex justifyContent="center" alignItems="center" minH="50vh">
          <Text>Лид не найден</Text>
        </Flex>
      </Layout>
    );
  }

  return (
    <Layout>
      <Flex flexDirection="column" gap="24px" paddingRight="32px" maxH="90vh" overflowY="auto">
        <Text color="gray.500" cursor="pointer" onClick={() => navigate(`/leads/${id}`)} display="flex" alignItems="center" gap="8px">
          <ArrowBackIcon />
          Назад к лиду
        </Text>
        <Flex flexDirection="column" gap="4px">
          <Text fontSize={30} lineHeight="36px" fontWeight={700} color="#3b515e">
            Редактирование лида
          </Text>
          <Text fontSize={16} lineHeight="24px" fontWeight={400} color="#65758b">
            Измените информацию о лиде
          </Text>
        </Flex>
        <CreateLeadBlock formik={formik} contacts={contacts} />
        <Flex gap="16px" justifyContent="flex-end">
          <Button onClick={() => navigate(`/leads/${id}`)}>Отмена</Button>
          <Button
            bg="#2563eb"
            color="white"
            _hover={{ bg: "#2563eb" }}
            _active={{ bg: "#2563eb" }}
            onClick={handleUpdate}
            isLoading={isUpdating}
            isDisabled={formik.values.name === "" || formik.values.responsible_user_id === null || formik.values.status === null || !hasChanges}
          >
            Сохранить изменения
          </Button>
        </Flex>
      </Flex>
    </Layout>
  );
};
